import { toDateString } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { type ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useState } from 'react'
import { toast } from 'sonner'
import { answerValueClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationDatePicker } from '#src/components/rich-text-editor/ObservationNode/ObservationDatePicker.tsx'
import { ObservationValueDisplay } from '#src/components/rich-text-editor/ObservationNode/ObservationDisplay.tsx'
import { ObservationInlineEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationInlineEditor.tsx'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import {
  captureRecordFor,
  isForeignRecord,
} from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function ObservationDisplayClickable({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  const { reference, draft, scaleType } = attributes
  invariant(reference || draft, 'reference or draft is required')

  const { observation } = useNodeRecord()
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const isReadOnly = useIsReadOnly()
  const [editing, setEditing] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const stopEditing = useCallback(() => setEditing(false), [])
  const updateAttributes = capture.editable ? props.updateAttributes : undefined
  // Another participant's record (pasted in, or the participant changed):
  // shown and clearable, not changed from here.
  const foreign = isForeignRecord(observation, captureRecordFor(capture))

  // Where the note cannot be changed, or there is nothing left to change,
  // a click goes straight to the details as it always has.
  const editsInline =
    inlineCapture &&
    !isReadOnly &&
    !foreign &&
    capture.editable &&
    !!observation &&
    !observation.isEnteredInError &&
    !observation.hasComponents &&
    // A date is changed in its picker; other `Qn` values in the details.
    (attributes.valueKind
      ? true
      : scaleType !== null &&
        scaleType !== 'Qn' &&
        (scaleType !== 'Ref' || !!attributes.referenceResourceType))

  // A recorded date is its own picker's trigger at rest: a click on the
  // label focuses it, and only a click (or Enter, Space) on it opens the
  // calendar.
  if (editsInline && attributes.valueKind) {
    return (
      <ObservationDateValueEditor
        observation={observation}
        attributes={attributes}
        updateAttributes={updateAttributes}
      />
    )
  }

  if (editing && editsInline) {
    return (
      <ObservationInlineEditor
        observation={observation}
        attributes={attributes}
        held={detailsOpen}
        onDone={stopEditing}
        onDetailsOpenChange={setDetailsOpen}
        updateAttributes={updateAttributes}
      />
    )
  }

  // At rest a click, Enter or Space opens the line for changes, or else
  // opens the details directly. A note that cannot be changed shows the
  // answer only.
  return (
    <ObservationNodeFrame
      attributes={attributes}
      className="pr-1"
      details={
        !capture.editable ? 'none' : editsInline || foreign ? 'button' : 'click'
      }
      onClick={
        foreign || !capture.editable ? undefined : () => setEditing(true)
      }
      foreign={foreign}
      updateAttributes={updateAttributes}
      participant={observation?.subject}
      layout={scaleType === 'Nar' ? 'block' : 'inline'}
    >
      <ObservationValueDisplay
        observation={observation}
        scaleType={scaleType}
        noteOnOneLine={inlineCapture}
      />
    </ObservationNodeFrame>
  )
}

// A recorded date whose value opens its picker, to be changed in place.
function ObservationDateValueEditor({
  observation,
  attributes,
  updateAttributes,
}: {
  observation: ExtendedObservation
  attributes: ObservationNodeAttributes
  updateAttributes?: (attributes: Partial<ObservationNodeAttributes>) => void
}): JSX.Element {
  const { patch } = useNodeRecord()
  const recorded = observation.valueDateTime
  return (
    <ObservationNodeFrame
      attributes={attributes}
      className="pr-1"
      updateAttributes={updateAttributes}
      participant={observation.subject}
    >
      <ObservationDatePicker
        value={recorded ? toDateString(recorded) : null}
        onPick={(day) =>
          patch((o) => {
            o.valueDateTime = day
          }).catch((cause: unknown) => {
            toast.error(
              `Failed to change ${attributes.codingDisplay}${cause instanceof Error ? `: ${cause.message}` : ''}`
            )
          })
        }
      >
        <button type="button" className={answerValueClass()}>
          {observation.valueDisplay}
        </button>
      </ObservationDatePicker>
    </ObservationNodeFrame>
  )
}
